import { SectionDescriptor } from '@shopify/polaris/build/ts/src/types'
import React, { useContext } from 'react'
import useSWR, { SWRConfiguration } from 'swr'

import { backendFetchResultData } from '../common/api'
import { GQLPaginator } from '../common/gql-models-common'
import { anyWindow } from '../common/helpers'
import { sellingPlanGroupsEmptyData } from '../common/new-user-test-data'
import { urlWithParams } from '../common/urls'

export interface SellingPlanGroup {
  id: string
  merchantCode: string
  summary: string
  productsCount: {
    count: number
  }
}

export const fetchSellingPlanGroups = (
  _: string,
  sortKey: string,
  beforeCursor: string,
  afterCursor: string,
  search: string = ''
): Promise<GQLPaginator<SellingPlanGroup>> => {
  if (anyWindow.newUserTest) {
    return Promise.resolve(sellingPlanGroupsEmptyData)
  }

  return backendFetchResultData(
    'GET',
    urlWithParams('/plans', {
      'sort-key': sortKey,
      before: beforeCursor,
      after: afterCursor,
      search: search || undefined,
    })
  )
}

export const useSellingPlanGroupsSWR = (
  sortKey: string,
  beforeCursor: string,
  afterCursor: string,
  search: string = '',
  swrConfig?: SWRConfiguration
) =>
  useSWR(
    ['selling-plan-groups', sortKey, beforeCursor, afterCursor, search],
    fetchSellingPlanGroups,
    swrConfig
  )

interface SellingPlanOption {
  name: string
  gid: string
}

interface SellingPlanGroupOption {
  gid: string
  name: string
  sellingPlans: SellingPlanOption[]
}

export const sellingPlanOptionListSections = (
  data: SellingPlanGroupOption[],
  disabled?: boolean
): SectionDescriptor[] =>
  data.map((spg: SellingPlanGroupOption) => ({
    title: spg.name,
    options: spg.sellingPlans.map((sp: SellingPlanOption) => ({
      label: sp.name,
      value: sp.gid,
      disabled,
    })),
  }))

export const sellingPlanGroupOptions = (data: SellingPlanGroupOption[]) =>
  data.map((spg: SellingPlanGroupOption) => ({
    label: spg.name,
    value: spg.gid,
  }))

export const sellingPlanNames = (spgData: SellingPlanGroupOption[]) => {
  const ret: Record<string, string> = {}

  spgData.forEach((spg: SellingPlanGroupOption) => {
    ret[spg.gid] = spg.name

    spg.sellingPlans.forEach((sp: SellingPlanOption) => (ret[sp.gid] = `${spg.name}: ${sp.name}`))
  })

  return ret
}

const fetchSellingPlanGroupsData = (_: string): Promise<SellingPlanGroupOption[]> =>
  backendFetchResultData('GET', `/plans/workflows-page-data`)

export const useSellingPlansSWR = () => useSWR(['selling-plans-data'], fetchSellingPlanGroupsData)

export const SellingPlansContext = React.createContext<SellingPlanGroupOption[]>([])

export const useSellingPlans = () => useContext(SellingPlansContext)

export const useSellingPlanNames = () => {
  const data = useSellingPlans()
  return sellingPlanNames(data)
}
